/**
 * Detects a Google Maps sub-module load failure (story #11887).
 *
 * The app preloads only the Maps JS base script at start-up. The SDK fetches
 * the parts that draw a map, its pin and the geocoder (`map`, `marker`,
 * `geocoder`, …) later, on first use, by injecting its own `<script>` tags.
 * When one of those fetches fails (a dropped connection, an ad blocker, a
 * captive Wi-Fi), the SDK throws `Could not load "<module>".` from that
 * script's `onerror`, in a task of its own. No try/catch in our code can see
 * it, and `window.google.maps` is still present, so left to the global
 * handler it replaces the page with the Error 206 screen.
 *
 * Measured in a headless Chrome harness with the app's zone.js: the
 * constructors (`new google.maps.Map/Marker/Geocoder()`) do not throw, a
 * geocode never calls back, `importLibrary('maps' | 'geocoding')` still
 * resolves, and the SDK never requests a failed module again in the same
 * page. So the failure is recognised here and reported to
 * `GoogleMapsLoaderService`, which tells the map consumers and loads the SDK
 * again on its next use.
 *
 * Only Google's own error counts: the exact message and a
 * `maps.googleapis.com` frame on the stack. Zone.js can hand the error over
 * wrapped, with the original on `rejection`, so both shapes are checked.
 */

const MAPS_MODULE_LOAD_MESSAGE = /^Could not load "([^"]+)"\.$/;
const MAPS_SCRIPT_HOST = 'maps.googleapis.com';

/** The module that failed to load, or `null` for any other error. */
export function mapsModuleLoadFailure(error: unknown): string | null {
  if (!error || typeof error !== 'object') {
    return null;
  }

  const err = error as {
    message?: unknown;
    stack?: unknown;
    rejection?: unknown;
  };
  const candidate =
    err.rejection && typeof err.rejection === 'object'
      ? (err.rejection as { message?: unknown; stack?: unknown })
      : err;

  if (
    typeof candidate.message !== 'string' ||
    typeof candidate.stack !== 'string' ||
    !candidate.stack.includes(MAPS_SCRIPT_HOST)
  ) {
    return null;
  }

  return MAPS_MODULE_LOAD_MESSAGE.exec(candidate.message)?.[1] ?? null;
}
